'use client';

import * as React from 'react';
import { useRoomContext } from '@livekit/components-react';
import { Smile } from 'lucide-react';
import { ControlBarAnchoredDropdown } from './control-bar-anchored-menu';
import { ReactionType, useReactions } from './ReactionsContext';

const AVAILABLE_REACTIONS: { type: ReactionType; label: string }[] = [
  { type: '👏', label: 'Palmas' },
  { type: '👍', label: 'Joinha' },
  { type: '❤️', label: 'Coração' },
  { type: '😂', label: 'Risada' },
  { type: '😮', label: 'Surpreso' },
];

interface ReactionsButtonProps {
  inline?: boolean;
  className?: string;
}

export function ReactionsButton({ inline = false, className }: ReactionsButtonProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const room = useRoomContext();
  const { addReaction } = useReactions();

  const sendReaction = React.useCallback(
    (type: ReactionType) => {
      if (!room?.localParticipant) return;

      const participantId = room.localParticipant.identity;
      const participantName = room.localParticipant.name || participantId;
      const reaction: ReactionType = type;

      // Adiciona a reação localmente
      addReaction({
        id: `${participantId}-${Date.now()}`,
        type: reaction,
        participantId,
        participantName,
        timestamp: Date.now(),
      });

      // Envia via data channel para outros participantes
      try {
        const data = {
          type: 'reaction',
          reaction,
          participantId,
          participantName,
          timestamp: Date.now(),
        };

        room.localParticipant
          .publishData(new TextEncoder().encode(JSON.stringify(data)), {
            reliable: true,
            topic: 'reactions',
          })
          .catch((error) => {
            console.error('Erro ao enviar reação:', error);
          });
      } catch (error) {
        console.error('Erro ao publicar reação:', error);
      }

      setIsOpen(false);
    },
    [room, addReaction],
  );

  // Fechar menu ao clicar fora
  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={className ?? 'lk-button lk-button-icon'}
        data-reactions-button="true"
        aria-label="Reações"
        aria-expanded={isOpen}
      >
        <Smile size={20} strokeWidth={2} />
      </button>

      <ControlBarAnchoredDropdown
        isOpen={isOpen}
        anchorRef={buttonRef}
        menuRef={menuRef}
        className="em-reactions-menu"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          borderRadius: '12px',
          padding: '12px',
          display: 'flex',
          gap: '8px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {AVAILABLE_REACTIONS.map((reaction) => (
          <button
            key={reaction.type}
            type="button"
            onClick={() => sendReaction(reaction.type)}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '8px',
              padding: '12px',
              cursor: 'pointer',
              fontSize: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              minWidth: '48px',
              minHeight: '48px',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              (e.currentTarget as HTMLElement).style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
            }}
            aria-label={reaction.label}
            title={reaction.label}
          >
            {reaction.type}
          </button>
        ))}
      </ControlBarAnchoredDropdown>
    </>
  );
}
